import {
  Box,
  Button,
  Divider,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  TextField,
  Tabs,
  Tab,
  Menu,
  Popover,
  IconButton,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Position, useEdges, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import { PlusCircle, Trash, CaretDown, Gear, XCircle, Eye } from '@phosphor-icons/react';
import GridLayout, { Layout, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { styled } from 'styled-components';

const ResponsiveGridLayout = WidthProvider(GridLayout) as any;
import { useAuthStore } from '../../store/storeAuth';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { axiosInstance } from '../../service/ApiService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import HeaderFooterReportBuilder from './HeaderFooterReportBuilder';
import PreviewModal from '../UI/PreviewModal/PreviewModal';

const saveTypes = [
  {
    name: 'Save as .txt',
    extension: '.txt',
    fileType: 'txt',
    mimeType: 'text/plain',
  },
  {
    name: 'Save as .html',
    extension: '.html',
    fileType: 'html',
    mimeType: 'text/html',
  },
  {
    name: 'Save as .json',
    extension: '.json',
    fileType: 'json',
    mimeType: 'application/json',
  },
  {
    name: 'Save as .csv',
    extension: '.csv',
    fileType: 'csv',
    mimeType: 'text/csv',
  },
  {
    name: 'Save as .xml',
    extension: '.xml',
    fileType: 'xml',
    mimeType: 'application/xml',
  },
  {
    name: 'Save as .yaml',
    extension: '.yaml',
    fileType: 'yaml',
    mimeType: 'text/yaml',
  },
  {
    name: 'Save as .pdf',
    extension: '.pdf',
    fileType: 'pdf',
    mimeType: 'application/pdf',
  },
  {
    name: 'Save as .docx',
    extension: '.docx',
    fileType: 'docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  {
    name: 'Save as .png',
    extension: '.png',
    fileType: 'png',
    mimeType: 'image/png',
  },
  {
    name: 'Save as .jpg',
    extension: '.jpg',
    fileType: 'jpg',
    mimeType: 'image/jpeg',
  },
  {
    name: 'Save as .webp',
    extension: '.webp',
    fileType: 'webp',
    mimeType: 'image/webp',
  },
];

const BlueSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'rgba(54, 80, 222, 1)',
  },
}));

const imageUrlCache = new Map<string, string>();

const processImageContent = (text: string): string => {
  const imageRegex = /(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff|ico))/gi;
  
  return text.replace(imageRegex, (match) => {
    if (!imageUrlCache.has(match)) {
      const randomStr = Math.random().toString(36).substring(2, 10);
      const srcWithRandom = `${match}?v=${randomStr}`;
      imageUrlCache.set(match, srcWithRandom);
    }
    
    const cachedUrl = imageUrlCache.get(match)!;
    return `<img src="${cachedUrl}" id="image-${match}" loading="eager" decoding="sync" style="max-width: 100%; height: auto;" />`;
  });
};

const processHeaderFooterContent = (html: string): string => {
  if (!html) return html;
  
  const trimmedHtml = html.trim();
  if (!trimmedHtml) return html;
  
  const hasImage = /<img/i.test(trimmedHtml);
  
  if (!hasImage) {
    return html;
  }
  
  let processedHtml = html.replace(/<img([^>]*)>/gi, (match, attributes) => {
    const srcMatch = attributes.match(/src=["']([^"']+)["']/i);
    if (!srcMatch) {
      console.warn('Image tag found but no src attribute:', match);
      return match;
    }
    
    const src = srcMatch[1];
    if (!src || src.trim() === '') {
      console.warn('Image src is empty:', match);
      return match;
    }
    
    const altMatch = attributes.match(/alt=["']([^"']*)["']/i);
    const alt = altMatch ? altMatch[1] : '';
    
    const styleMatch = attributes.match(/style=["']([^"']+)["']/i);
    let style = styleMatch ? styleMatch[1] : '';
    
    if (!style.includes('max-height')) {
      style = style ? `${style}; max-height: 80pt;` : 'max-height: 80pt;';
    }
    if (!style.includes('max-width')) {
      style = style ? `${style}; max-width: 100%;` : 'max-width: 100%;';
    }
    if (!style.includes('height')) {
      style = style ? `${style}; height: auto;` : 'height: auto;';
    }
    if (!style.includes('width')) {
      style = style ? `${style}; width: auto;` : 'width: auto;';
    }
    if (!style.includes('object-fit')) {
      style = style ? `${style}; object-fit: contain;` : 'object-fit: contain;';
    }
    if (!style.includes('vertical-align')) {
      style = style ? `${style}; vertical-align: middle;` : 'vertical-align: middle;';
    }
    if (!style.includes('display')) {
      style = style ? `${style}; display: inline-block;` : 'display: inline-block;';
    }
    
    const altAttr = alt ? ` alt="${alt}"` : '';
    return `<img src="${src}"${altAttr} style="${style}" />`;
  });
  
  processedHtml = processedHtml.replace(/<p[^>]*>\s*<\/p>/gi, '');
  processedHtml = processedHtml.replace(/<p[^>]*>(\s*<img[^>]*>\s*)<\/p>/gi, '$1');
  processedHtml = processedHtml.replace(/<p[^>]*>(\s*<br[^>]*>\s*<img[^>]*>\s*)<\/p>/gi, '$1');
  processedHtml = processedHtml.replace(/<p[^>]*>(\s*<img[^>]*>\s*<br[^>]*>\s*)<\/p>/gi, '$1');
  
  processedHtml = processedHtml.replace(/<div[^>]*>\s*<\/div>/gi, '');
  processedHtml = processedHtml.replace(/<span[^>]*>\s*<\/span>/gi, '');
  
  processedHtml = processedHtml.trim();
  
  
  const imageOnlyMatch = processedHtml.match(/^\s*(<img[^>]*>)\s*$/i);
  if (imageOnlyMatch) {
    return imageOnlyMatch[1];
  }
  
  return processedHtml;
};

const StyledTabs = styled(Tabs)<{ theme: string }>`
  & .MuiTabs-root {
    min-height: 40px;
  }

  & .MuiTabs-indicator {
    background-color: #3650DE;
    height: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  & .MuiTab-root {
    text-transform: none;
    min-height: 40px;
    padding: 8px 16px;
    color: ${props => props.theme === 'dark' ? '#98A2B3' : '#667085'};
    font-weight: 500;
    font-size: 14px;

    &.Mui-selected {
      color: ${props => props.theme === 'dark' ? '#D0D5DD' : '#101828'};
      font-weight: 600;
    }

    &:hover {
      color: ${props => props.theme === 'dark' ? '#D0D5DD' : '#344054'};
    }
  }

  & .MuiTabs-flexContainer {
    border-bottom: 1px solid ${props => props.theme === 'dark' ? '#475467' : '#EAECF0'};
  }

  & .MuiTabs-indicator {
    width: 50% !important;
    left: 0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: left;
  }

  &[data-active-tab="0"] .MuiTabs-indicator {
    transform: translateX(0%);
  }

  &[data-active-tab="1"] .MuiTabs-indicator {
    transform: translateX(100%);
  }

  &.MuiTabs-fullWidth .MuiTabs-indicator {
    width: 50% !important;
    left: 0 !important;
  }
`;

const GridLayoutContainer = styled(Box)<{ theme: string }>`
  border: 2px dashed ${props => props.theme === 'dark' ? '#475467' : '#D0D5DD'};
  border-radius: 8px;
  background: ${props => props.theme === 'dark' ? '#101828' : '#F9FAFB'};
  position: relative;
  height: 100%;
  overflow: hidden;

  .react-grid-layout {
    position: relative;
    height: 100%;
  }

  .react-grid-item {
    transition: all 200ms ease;
    transition-property: left, top, width, height;
  }

  .react-grid-item.cssTransforms {
    transition-property: transform, width, height;
  }

  .react-grid-item.resizing {
    z-index: 1;
    will-change: width, height;
  }

  .react-grid-item.react-draggable-dragging {
    transition: none;
    z-index: 3;
    will-change: transform;
  }

  .react-grid-item.dropping {
    visibility: hidden;
  }

  .react-grid-item.react-grid-placeholder {
    background: ${props => props.theme === 'dark' ? '#344054' : '#E7F0FF'};
    border: 2px dashed ${props => props.theme === 'dark' ? '#475467' : '#3650DE'};
    border-radius: 8px;
    opacity: 0.2;
    transition-duration: 100ms;
    z-index: 2;
    user-select: none;
  }
`;

function getInitialSavingTypeByFileLinkName(extension: string | undefined) {
  if (extension) {
    const savedType = saveTypes.find(saveType => saveType.extension === extension)
    if (savedType) {
      return savedType.name
    }
  }
  return saveTypes[0].name
}

interface DisplayVariable {
  id: string;
  name: string;
  textData?: any[];
  x: number;
  y: number;
  w: number;
  h: number;
  settings?: {
    setting1?: boolean;
    setting2?: boolean;
    autoFormat?: boolean;
    preserveLineBreaks?: boolean;
    dynamicHeight?: boolean;
  };
}

interface HeaderFooterFormData {
  leftHeader: string;
  centerHeader: string;
  rightHeader: string;
  leftFooter: string;
  centerFooter: string;
  rightFooter: string;
  headerBackgroundImage: string | null;
  footerBackgroundImage: string | null;
}

function ReportBuilderNode({ data, type, id }: NodeProps) {
  const edges = useEdges();
  const { setSaving, flowId, setSchema, onEdgeDelete, setEdges } = useFlowsStore(state => state);
  const { setNodes: setNodeState, getNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const user = useAuthStore(state => state.user);
  const setNotification = useNotificationStore(state => state.setNotification);


  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);
  const [savingType, setSavingType] = useState(getInitialSavingTypeByFileLinkName(data?.fileExtension));
  const [size, setSize] = useState({
    height: data?.size?.height || 600,
    width: data?.size?.width || 800,
  });
  const [filter, setFilter] = useState(data.filter || 'all');
  const [saveOutput, setSaveOutput] = useState(data?.saveOutput ?? false);
  const [collapsed, setCollapsed] = useState(true);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [activeTab, setActiveTab] = useState(data?.activeTab ?? 0);
  
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const openSettingsPopover = Boolean(settingsAnchorEl);
  const popOverSettingsId = openSettingsPopover ? 'variable-settings-popover' : undefined;
  const [activeVariableId, setActiveVariableId] = useState<string | null>(null);
  
  const [isConfigPopoverOpen, setIsConfigPopoverOpen] = useState(false);
  const [configAnchorEl, setConfigAnchorEl] = useState<HTMLElement | null>(null);
  
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  
  const [headerFooterFormData, setHeaderFooterFormData] = useState<HeaderFooterFormData>({
    leftHeader: data?.headerFooterConfig?.leftHeader || '',
    centerHeader: data?.headerFooterConfig?.centerHeader || '',
    rightHeader: data?.headerFooterConfig?.rightHeader || '',
    leftFooter: data?.headerFooterConfig?.leftFooter || '',
    centerFooter: data?.headerFooterConfig?.centerFooter || '',
    rightFooter: data?.headerFooterConfig?.rightFooter || '',
    headerBackgroundImage: data?.headerFooterConfig?.headerBackgroundImage || null,
    footerBackgroundImage: data?.headerFooterConfig?.footerBackgroundImage || null,
  });
  
  const [variables, setVariables] = useState<DisplayVariable[]>([]);

  const debounceTimeoutRef = useRef<any>(null);
  const savingTypeRef = useRef<null | string>(savingType);
  const isGridChangeRef = useRef(false);
  const variablesRef = useRef(variables);
  const layoutChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const prevEdgesRef = useRef<any[]>([]);

  useEffect(() => {
    if (prevEdgesRef.current.length === 0) {
      prevEdgesRef.current = edges;
      return;
    }
    
    const removedEdges = prevEdgesRef.current.filter(
      prevEdge => !edges.some(edge => edge.id === prevEdge.id)
    ).filter(edge => edge.target === id); 
    
    if (removedEdges.length > 0) {
      
      setVariables(currentVariables => {
        const updatedVariables = currentVariables.map(variable => {
          const hadRemovedConnection = removedEdges.some(
            edge => edge.targetHandle === variable.id
          );
          
          if (hadRemovedConnection) {
            return {
              ...variable,
              textData: []
            };
          }
          return variable;
        });
        
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowId,
          setSaving,
          objectData: {
            variables: updatedVariables,
          },
          setSchema,
        });
        
        return updatedVariables;
      });
    }
    
    prevEdgesRef.current = edges;
  }, [edges, id, setNodeState, setSaving, flowId, setSchema]);

  useEffect(() => {
    
    if (!isChangingDirectly) {
      if (data?.variables && Array.isArray(data.variables)) {
        setVariables(data.variables);
      } else {
        
        let legacyTextData: any[] = [];
        
        if (data?.textData && Array.isArray(data.textData)) {
          legacyTextData.push(...data.textData);
        }
        
        if (data?.contentIn && typeof data.contentIn === 'string') {
          const connectedEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'contentIn');
          if (connectedEdge) {
            legacyTextData.push({
              text: data.contentIn,
              id: connectedEdge.source,
              label: 'Legacy Data'
            });
          }
        }
        
        const defaultVariable: DisplayVariable = {
          id: 'contentIn', 
          name: 'Display 1',
          textData: legacyTextData,
          x: 0,
          y: 0,
          w: 12, 
          h: 4,
          settings: {
            dynamicHeight: true, 
          }
        };
        setVariables([defaultVariable]);
        
        updateNodeInternals(id);
        
        const cleanupData: any = {
          variables: [defaultVariable],
          isNewNode: true, 
        };
        
        if (data?.contentIn) {
          cleanupData.contentIn = null;
        }
        
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: cleanupData,
          setSchema,
        });
      }
    }
  }, [data?.variables, isChangingDirectly]);

  useEffect(() => {
    updateNodeInternals(id);
  }, [variables, id, updateNodeInternals]);

  const [variablesToSave, setVariablesToSave] = useState<DisplayVariable[] | null>(null);

  useEffect(() => {
    if (variablesToSave) {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: {
          variables: variablesToSave,
        },
        setSchema,
      });
      setVariablesToSave(null);
    }
  }, [variablesToSave, edges, setNodeState, id, setSaving, flowId, setSchema]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    return () => {
      if (layoutChangeTimeoutRef.current) {
        clearTimeout(layoutChangeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (variables.length === 0) return;


    const inputData = data?.textData || [];
    
    const updatedVariables = variables.map(variable => {
      const edge = edges.find(e => e.target === id && e.targetHandle === variable.id);
      if (!edge) return variable;
      
      const connectedData = inputData.filter((item: any) => item.id === edge.source);
      
      if (connectedData.length === 0) {
        const sourceNode = getNodes().find(n => n.id === edge.source);
        if (sourceNode?.data?.text) {
          connectedData.push({ 
            text: sourceNode.data.text, 
            id: sourceNode.id, 
            label: sourceNode.data.label || '' 
          });
        } else if (sourceNode?.data?.textData && Array.isArray(sourceNode.data.textData)) {
          connectedData.push(...sourceNode.data.textData);
        }
      }
      
      const currentDataStr = JSON.stringify(variable.textData || []);
      const newDataStr = JSON.stringify(connectedData);
      
      if (currentDataStr !== newDataStr) {
        return {
          ...variable,
          textData: connectedData
        };
      }
      
      return variable;
    });
    
    setVariables(updatedVariables);
  }, [data.textData, edges, variables.length, id, getNodes]);


  useEffect(() => {
    const timer = setTimeout(() => {
      updateNodeInternals(id);
    }, 50);
    
    const longerTimer = setTimeout(() => {
      updateNodeInternals(id);
    }, 200);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(longerTimer);
    };
  }, [activeTab, id, updateNodeInternals]);

  const variablesToLayout = useCallback((vars: DisplayVariable[]): Layout[] => {
    return vars.map(variable => ({
      i: variable.id,
      x: variable.x,
      y: variable.y,
      w: variable.w,
      h: variable.h,
      minW: 1,
      minH: 1,
    }));
  }, []);

  const currentLayout = useMemo(() => variablesToLayout(variables), [variables, variablesToLayout]);

  const constrainLayoutItem = useCallback((layoutItem: Layout): Layout => {
    const containerHeight = gridContainerRef.current?.clientHeight || 0;
    const rowHeight = 50;
    const margin = 5;
    const maxRows = containerHeight > 0 ? Math.floor(containerHeight / (rowHeight + margin)) : Infinity;
    
    if (maxRows === Infinity) {
      return layoutItem;
    }
    
    let constrainedY = layoutItem.y;
    let constrainedH = layoutItem.h;
    
    constrainedH = Math.min(constrainedH, maxRows);
    constrainedY = Math.min(constrainedY, maxRows - constrainedH);
    constrainedY = Math.max(0, constrainedY);
    constrainedH = Math.max(1, constrainedH);
    
    return {
      ...layoutItem,
      y: constrainedY,
      h: constrainedH,
    };
  }, []);

  const handleLayoutChange = useCallback((layout: Layout[]) => {
    if (isGridChangeRef.current) return;
    
    if (layoutChangeTimeoutRef.current) {
      clearTimeout(layoutChangeTimeoutRef.current);
    }
    
    layoutChangeTimeoutRef.current = setTimeout(() => {
      isGridChangeRef.current = true;
      
      const updatedVariables = variablesRef.current.map(variable => {
        const layoutItem = layout.find(item => item.i === variable.id);
        if (layoutItem) {
          const constrainedItem = constrainLayoutItem(layoutItem);
          return {
            ...variable,
            x: constrainedItem.x,
            y: constrainedItem.y,
            w: constrainedItem.w,
            h: constrainedItem.h,
          };
        }
        return variable;
      });

      setVariables(updatedVariables);
      
      setTimeout(() => {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowId,
          setSaving,
          objectData: {
            variables: updatedVariables,
          },
          setSchema,
        });
        isGridChangeRef.current = false;
      }, 0);
    }, 150);
  }, [edges, id, flowId, setSaving, setNodeState, setSchema, constrainLayoutItem]); 

  const GridItem = memo(({ variable }: { variable: DisplayVariable }) => {
    const variableSettings = {
      dynamicHeight: true,
      ...variable.settings,
    };

    const hasContent = variable.textData && variable.textData.length > 0;
    
    const textDataKey = useMemo(() => JSON.stringify(variable.textData || []), [variable.textData]);
    const processedContent = useMemo(() => {
      if (!hasContent) return '';
      const textContent = (variable.textData || []).map((item: any) => item.text || '').join('\n');
      return processImageContent(textContent);
    }, [hasContent, textDataKey]);

    return (
      <Box
        className="grid-item-content"
        sx={{
          background: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
          border: `2px solid ${user?.theme === 'dark' ? '#344054' : '#E0E0E0'}`,
          borderRadius: '8px',
          color: user?.theme === 'dark' ? '#D0D5DD' : '#667085',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          '&:hover': {
            borderColor: user?.theme === 'dark' ? '#475467' : '#D0D5DD',
          },
        }}
      >
        <Box
          className="grid-item-drag-handle"
          sx={{
            padding: '8px 12px',
            borderBottom: `1px solid ${user?.theme === 'dark' ? '#344054' : '#E0E0E0'}`,
            background: user?.theme === 'dark' ? '#344054' : '#F9FAFB',
            fontWeight: 600,
            fontSize: '12px',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#667085',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            cursor: 'move',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: user?.theme === 'dark' ? '#D0D5DD' : '#667085',
            }}
          >
            {variable.name}
          </Typography>
          <Box className="nodrag" onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
            <IconButtonTooltip
              title="Display Settings"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                handleGridSettingsClick(variable.id);
              }}
            >
              <Gear size={16} color="#98A2B3" />
            </IconButtonTooltip>
          </Box>
        </Box>
        <Box
          sx={{
            padding: '12px',
            overflow: 'auto',
            flex: 1,
            fontSize: '12px',
            lineHeight: 1.4,
            ...(hasContent
              ? {
                  whiteSpace: 'pre-wrap',
                  '& *': {
                    maxWidth: '100%',
                  },
                }
              : {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: user?.theme === 'dark' ? '#667085' : '#98A2B3',
                  fontStyle: 'italic',
                }),
          }}
          {...(hasContent
            ? { dangerouslySetInnerHTML: { __html: processedContent.replace(/\n/g, '<br>') } }
            : { children: 'Connect data to this variable' })}
        />
      </Box>
    );
  }, (prevProps, nextProps) => {
    const prev = prevProps.variable;
    const next = nextProps.variable;
    
    const layoutChanged = prev.x !== next.x || prev.y !== next.y || prev.w !== next.w || prev.h !== next.h;
    
    const prevTextData = JSON.stringify(prev.textData || []);
    const nextTextData = JSON.stringify(next.textData || []);
    const contentChanged = prevTextData !== nextTextData;
    
    const nameChanged = prev.name !== next.name;
    const settingsChanged = JSON.stringify(prev.settings || {}) !== JSON.stringify(next.settings || {});
    
    if (layoutChanged && !contentChanged && !nameChanged && !settingsChanged) {
      return true;
    }
    
    return false; 
  });


  const addVariable = () => {
    const newVariable: DisplayVariable = {
      id: variables.length === 0 ? 'contentIn' : 'var-' + Math.random().toString(36).substr(2, 9),
      name: `Display ${variables.length + 1}`,
      textData: [],
      x: 0,
      y: variables.length * 5, 
      w: 12, 
      h: 4,
      settings: {
        dynamicHeight: true, 
      }
    };

    const updatedVariables = [...variables, newVariable];
    setVariables(updatedVariables);

    updateNodeInternals(id);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        variables: updatedVariables,
        isNewNode: true, 
      },
      setSchema,
    });

  };

  const handleVariableChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    varId: string,
  ) => {
    const updatedVariables = variables.map(variable => {
      if (variable.id === varId) {
        return { ...variable, [e.target.name]: e.target.value };
      }
      return variable;
    });

    setVariables(updatedVariables);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        variables: updatedVariables,
      },
      setSchema,
    });
  };

  const deleteVariable = (varId: string) => {
    if (variables.length <= 1) {
      return;
    }

    const updatedVariables = variables.filter(variable => variable.id !== varId);
    setVariables(updatedVariables);

    updateNodeInternals(id);


    const connectedEdges = edges.filter(
      (edge: any) => edge.target === id && edge.targetHandle === varId
    );
    
    if (connectedEdges.length > 0) {
      
      setEdges(eds => eds?.filter((e: any) => !connectedEdges.some(ce => ce.id === e.id)));
      
    connectedEdges.forEach((edge: any) => {
      onEdgeDelete([edge]);
    });
    }

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        variables: updatedVariables,
      },
      setSchema,
    });

  };

  const generateHTMLStructure = useCallback(() => {
    const variablesToUse = variables && variables.length > 0 ? variables : (data?.variables || []);
    
    if (!variablesToUse || variablesToUse.length === 0) {
      const inputData = data?.textData || [];
      const rawContent = inputData.map((item: any) => item?.text || '').join('\n');
      return processImageContent(rawContent);
    }

    const sortedVariables = [...variablesToUse].sort((a: DisplayVariable, b: DisplayVariable) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    const minY = Math.min(...sortedVariables.map(v => v.y));
    const maxY = Math.max(...sortedVariables.map(v => v.y + v.h - 1));
    
    const variablesByRow: { [key: number]: DisplayVariable[] } = {};
    sortedVariables.forEach(variable => {
      const rowIndex = variable.y;
      if (!variablesByRow[rowIndex]) {
        variablesByRow[rowIndex] = [];
      }
      variablesByRow[rowIndex].push(variable);
    });

    const maxX = Math.max(...sortedVariables.map(v => v.x + v.w), 0);
    const totalColumns = Math.max(maxX, 12);
    
    const rowHeight = 50;
    const rowMargin = 5;
    
    const rowsHtml: string[] = [];
    
    for (let y = minY; y <= maxY; y++) {
      const rowVariables = (variablesByRow[y] || []).sort((a, b) => a.x - b.x);
      
      const rowPositions = new Array(totalColumns).fill(null);
      
      rowVariables.forEach(variable => {
        if (variable.y === y) {
          for (let i = variable.x; i < variable.x + variable.w; i++) {
            if (i < rowPositions.length && !rowPositions[i]) {
              rowPositions[i] = variable;
            }
          }
        }
      });
      
      const rowDivs: string[] = [];
      
      const renderedVariables = new Set<DisplayVariable>();
      
      let col = 0;
      while (col < totalColumns) {
        const variable = rowPositions[col];
        
        if (variable && !renderedVariables.has(variable)) {
          renderedVariables.add(variable);
          
          const variableSettings = {
            dynamicHeight: true, 
            ...variable.settings,
          };
          
          const textContent = (variable.textData || [])
            .map((item: any) => (item && item.text) ? String(item.text).trim() : '')
            .filter((text: string) => text)
            .join('\n') || '';
          
          const processedContent = processImageContent(textContent);
          
          let heightStyle = '';
          
          if (!variableSettings.dynamicHeight) {
            const fixedHeightPixels = variable.h * rowHeight + (variable.h - 1) * rowMargin;
            heightStyle = `height: ${fixedHeightPixels}px; overflow-y: auto; overflow-x: hidden;`;
          } else {
            const minHeightPixels = variable.h * rowHeight;
            heightStyle = `min-height: ${minHeightPixels}px;`;
          }
          
          const flexBasis = (variable.w / totalColumns) * 100;
          rowDivs.push(`<div style="flex: 0 0 ${flexBasis}%; ${heightStyle} padding: 10px; box-sizing: border-box;">
            ${processedContent.replace(/\n/g, '<br>')}
          </div>`);
          
          col += variable.w;
        } else if (!variable) {
          let emptyCols = 0;
          while (col < totalColumns && !rowPositions[col]) {
            emptyCols++;
            col++;
          }
          
          const flexBasis = (emptyCols / totalColumns) * 100;
          rowDivs.push(`<div style="flex: 0 0 ${flexBasis}%;"></div>`);
        } else {
          col++;
        }
      }

      const rowStyle = `display: flex; gap: 5px; width: 100%; min-height: ${rowHeight}px; margin-bottom: ${rowMargin}px;`;
      
      rowsHtml.push(`<div class="row" style="${rowStyle}">
        ${rowDivs.join('')}
      </div>`);
    }

    return `<!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; line-height: 1.4; }
        .container { 
          width: 100%; 
          margin: 20px 0; 
        }
        .row {
          display: flex;
          gap: 5px;
          width: 100%;
        }
      </style>
    </head>
    <body>
      <div class="container">
        ${rowsHtml.join('')}
      </div>
    </body>
    </html>`;
  }, [variables, data?.variables, data?.textData]);


  const onExecute = useCallback(async () => {
    try {
      setIsLoading(true);

      const allTextData = variables.reduce((acc, variable) => {
        return [...acc, ...(variable.textData || [])];
      }, [] as any[]);
      
      const processedData = generateHTMLStructure();

      if (processedData.includes('<canvas')) {
        setNotification({
          type: 'warning',
          message: "Your content contains <canvas> elements, which aren't currently supported. Try to create your charts using d3.js library in HTML."
        })
      }

      let fileLink = null;

      if (saveOutput) {
        const findType = saveTypes.find(type => type.name === savingType);

        if (findType) {
          try {
            let endpoint = '/upload/text';
            const uploadText = findType.fileType === 'pdf' ? buildPdfHtml() : (processedData || '');
            let requestData: any = {
              text: uploadText,
              fileType: findType.fileType,
            };
            
            if (findType.fileType === 'pdf') {
              endpoint = '/upload/text-docraptor';
              requestData = {
                text: uploadText,
                fileType: findType.fileType,
              };
            }
            
            const { data: res } = await axiosInstance.post(endpoint, requestData);
            fileLink = res.data;
          } catch (uploadError: any) {
            console.error('Error uploading text:', uploadError);
            setNotification({
              type: 'error',
              message: `Failed to generate ${findType.extension} file: ${uploadError.message || 'Unknown error'}`
            });
          }
        }
      }

      const outputData = {
        text: processedData,
        textData: allTextData,
        variables: variables,
        fileLink: fileLink,
        isNewNode: true, 
      };

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        objectData: outputData,
        setSchema,
      });

      const contentOutEdge = edges?.find(edge => edge?.source === id && edge?.sourceHandle === 'contentOut');

      if (contentOutEdge) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id: contentOutEdge.target,
          flowId,
          setSaving,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectData: {
            text: processedData,
            textData: allTextData,
          },
          setSchema,
        });
      }

    } catch (error) {
      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId || '',
        setSchema,
        setNotification,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
    } finally {
      setIsLoading(false)
      setHasToExecute(true)
    }
  }, [variables, saveOutput, savingType, edges, executeCounter, flowId, id, setNodeState, setNotification, setSaving, setSchema, user?.objects, user?.spaceObjects, data, generateHTMLStructure]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onExecute();
        }, 2000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, data?.canceled, onExecute]);

  const onChangeSavingType = useCallback(
    (e: { target: { value: React.SetStateAction<string> } }) => {
      setSavingType(e.target.value);
      savingTypeRef.current = e.target.value as string
    },
    [setSavingType],
  );

  const onSaveOutputChange = async () => {
    setSaveOutput((prev: boolean) => !prev)
    updateNodeInternals(id)

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectData: {
        saveOutput: !saveOutput,
      },
      setSchema,
      setSaving,
    });

    if (!saveOutput) {
    const findType = saveTypes.find(type => type.name === savingType);
      const htmlStructure = generateHTMLStructure();

      if (findType && htmlStructure) {
        let endpoint = '/upload/text';
        const uploadText = findType.fileType === 'pdf' ? buildPdfHtml() : (htmlStructure || '');
        let requestData: any = {
          text: uploadText,
          fileType: findType.fileType,
        };
        
        if (findType.fileType === 'pdf') {
          endpoint = '/upload/text-docraptor';
          requestData = {
            text: uploadText,
            fileType: findType.fileType,
          };
        }
        
        const { data: res } = await axiosInstance.post(endpoint, requestData);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectData: {
        fileLink: res.data,
      },
      setSchema,
      setSaving,
    });
      }
    }
  };

  const onChangeFilter = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setFilter(event.target.value);
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      changeType: 'changeText',
      flowId,
      objectData: {
        filter: event.target.value,
      },
      setSchema,
    });
  };

  const onChangeInput = useCallback(
    (content: {
      target: { value: string | number; name: string | number };
    }) => {
      const fieldName = content.target.name as 'width' | 'height';
      const fieldValue = Number(content.target.value);
      
      setSize(prevSize => {
        const newSize = {
          ...prevSize,
          [fieldName]: fieldValue,
        };
        
        setTimeout(() => {
          setNodeState((currentNodes: any[]) => {
            return currentNodes.map(node => {
              if (node.id === id) {
                return {
                  ...node,
                  width: newSize.width,
                  height: newSize.height,
                  style: {
                    ...node.style,
                    width: newSize.width,
                    height: newSize.height,
                  },
                };
              }
              return node;
            });
          });
          
          
          setTimeout(() => {
            updateNodeInternals(id);
          }, 10);
        }, 0);
        
        return newSize;
      });
    },
    [id, setNodeState, updateNodeInternals],
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        activeTab: newValue,
      },
      setSchema,
    });
  };



  useDebounceEffect(
    () => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          size: {
            height: size.height,
            width: size.width,
          },
        },
        setSchema,
      });
    },
    [size, id, setNodeState, edges, setSaving, flowId, setSchema],
    500,
  );

  useDebounceEffect(() => {
    nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          fileExtension: saveTypes.find(type => type.name === savingType)?.extension,
        },
        setSchema,
    })
  }, [savingType], 500)

  const buildPdfHtml = useCallback((): string => {
     const baseHtml = generateHTMLStructure();
     
     const bodyMatch = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(baseHtml);
     const bodyContent = bodyMatch ? bodyMatch[1] : '';

     const headerBg = headerFooterFormData.headerBackgroundImage || '';
     const left = processHeaderFooterContent(headerFooterFormData.leftHeader || '');
     const center = processHeaderFooterContent(headerFooterFormData.centerHeader || '');
     const right = processHeaderFooterContent(headerFooterFormData.rightHeader || '');

     const leftContent = left || '';
     const centerContent = center || '';
     const rightContent = right || '';
     
     let headerInner = `<div style="display:flex; justify-content:space-between; align-items:center; width:100%;">
  <div class="header-left">${leftContent}</div>
  <div class="header-center">${centerContent}</div>
  <div class="header-right">${rightContent}</div>
</div>`;

     if (/\[pg\]/i.test(headerInner)) {
       headerInner = headerInner.replace(/\[pg\]/gi, '<div class="page-number"></div>');
     }

     const footerBg = headerFooterFormData.footerBackgroundImage || '';
     const leftFooter = processHeaderFooterContent(headerFooterFormData.leftFooter || '');
     const centerFooter = processHeaderFooterContent(headerFooterFormData.centerFooter || '');
     const rightFooter = processHeaderFooterContent(headerFooterFormData.rightFooter || '');

     const leftFooterContent = leftFooter || '';
     const centerFooterContent = centerFooter || '';
     const rightFooterContent = rightFooter || '';
     
     let footerInner = `<div style="display:flex; justify-content:space-between; align-items:center; width:100%;">
  <div class="footer-left">${leftFooterContent}</div>
  <div class="footer-center">${centerFooterContent}</div>
  <div class="footer-right">${rightFooterContent}</div>
</div>`;

     if (/\[pg\]/i.test(footerInner)) {
       footerInner = footerInner.replace(/\[pg\]/gi, '<div class="page-number"></div>');
     }

     const bgCss = headerBg ? `background: url("${headerBg}") no-repeat center top;` : '';
     const footerBgCss = footerBg ? `background: url("${footerBg}") no-repeat center bottom;` : '';

     const pdfDoc = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<style>
@page:first { margin-top: 120pt; }
@page {
  size: A4;
  margin: 120pt 40pt 40pt 40pt;
  @top-center { content: element(docHeader); }
  @bottom-center { content: element(docFooter); }
}
#docHeader {
  position: running(docHeader);
  ${bgCss}
  background-size: cover;
  background-position: center top;
  height: 100pt;
  display: flex;
  align-items: center;
  padding: 0 20pt;
  box-sizing: border-box;
  overflow: hidden;
}
@page:first #docHeader { padding-top: 80pt; }
#docHeader .page-number::after { content: counter(page); }
#docHeader .header-left,
#docHeader .header-center,
#docHeader .header-right {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1px;
  flex: 1;
}
#docHeader img { 
  max-height: 80pt; 
  max-width: 100%; 
  width: auto; 
  height: auto; 
  object-fit: contain; 
  vertical-align: middle; 
  display: inline-block;
}
#docFooter {
  position: running(docFooter);
  ${footerBgCss}
  background-size: cover;
  background-position: center bottom;
  height: 100pt;
  display: flex;
  align-items: center;
  padding: 0 20pt;
  box-sizing: border-box;
  overflow: hidden;
}
#docFooter .page-number::after { content: counter(page); }
#docFooter .footer-left,
#docFooter .footer-center,
#docFooter .footer-right {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1px;
  flex: 1;
}
#docFooter img { 
  max-height: 80pt; 
  max-width: 100%; 
  width: auto; 
  height: auto; 
  object-fit: contain; 
  vertical-align: middle; 
  display: inline-block;
}

body { 
  font-family: Arial, sans-serif; 
  margin: 0; 
  padding: 20px; 
  line-height: 1.4; 
}

.container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  margin: 20px 0;
  align-items: flex-start;
}
.container > div {
  box-sizing: border-box;
  padding: 10px;
  flex: 1 1 300px;
  max-width: 100%;
}
</style>
</head>
<body>

<div id="docHeader" style="display: flex; justify-content: space-between; align-items: center; z-index: -1;">
  ${headerInner}
</div>

<div id="docFooter" style="display: flex; justify-content: space-between; align-items: center; z-index: -1;">
  ${footerInner}
</div>

${bodyContent}

</body>
</html>`;

     return pdfDoc;
   }, [generateHTMLStructure, headerFooterFormData]);

  const onDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (!savingTypeRef.current) return;

      const findType = saveTypes.find(type => type.name === savingTypeRef.current);

      if (!findType) {
        return;
      }

      setIsLoading(true);
      try {
      const htmlStructure = generateHTMLStructure();

         
        let textToUpload = htmlStructure;
        if (findType.fileType === 'pdf') {
          textToUpload = buildPdfHtml();
        }

        let endpoint = '/upload/text';
        let requestData: any = {
          text: textToUpload || '',
          fileType: findType.fileType,
        };
        
        if (findType.fileType === 'pdf') {
          endpoint = '/upload/text-docraptor';
          requestData = {
            text: textToUpload || '',
            fileType: findType.fileType,
          };
        }
        
        const { data: { data: fileLink } } = await axiosInstance.post(endpoint, requestData);

        const { data: res } = await axiosInstance.get('/upload/presigned-url', {
          params: {
            url: fileLink.src,
          }
        });

        const urlContentBlob = new Blob([new Uint8Array(res.data.file.data).buffer], {
          type: findType.mimeType,
        });

        const url = URL.createObjectURL(urlContentBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileLink.name;
        document.body.appendChild(link);
        link.click();
        
        setNotification({ 
          type: 'success', 
          message: `Successfully downloaded ${findType.extension} file` 
        });
      } catch (error: any) {
        console.error('Download error:', error);
        
        let errorMessage = 'Unknown error';
        if (error.response?.data?.message) {
          errorMessage = Array.isArray(error.response.data.message) 
            ? error.response.data.message.join(', ') 
            : error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        if (findType.fileType === 'pdf' && errorMessage.includes('Browser was not found')) {
          setNotification({ 
            type: 'error', 
            message: 'PDF generation requires Chromium which is not available. Try downloading as HTML instead.' 
          });
        } else {
          setNotification({ 
            type: 'error', 
            message: `Failed to download ${findType.extension} file: ${errorMessage}` 
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [variables, data?.variables, data?.textData, setNotification, generateHTMLStructure, buildPdfHtml],
  );

  const nodeType = nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  const calculateMinHeight = () => {
    const baseHeight = 400; 
    const variableHeight = collapsed ? 60 : 20; 
    const variablesHeight = variables.length * variableHeight;
    const headerHeight = 120; 
    const controlsHeight = 220; 
    
    return Math.max(baseHeight, headerHeight + variablesHeight + controlsHeight);
  };

  useEffect(() => {
    const newHeight = calculateMinHeight();
    if (newHeight > size.height) {
      setSize(prevSize => ({ ...prevSize, height: newHeight }));
    }
  }, [variables.length, collapsed]);

  useEffect(() => {
    if (variables.length === 0) return;
    
    const edgesToFix = edges.filter(edge => 
      edge.target === id && 
      edge.targetHandle && 
      edge.targetHandle !== 'input' && 
      edge.targetHandle !== 'outputFlow' && 
      !variables.some(v => v.id === edge.targetHandle) 
    );
    
    if (edgesToFix.length > 0) {
      
      const updatedEdges = edges.map(edge => {
        if (edgesToFix.some(e => e.id === edge.id)) {
          return {
            ...edge,
            targetHandle: variables[0].id
          };
        }
        return edge;
      });
      
      setEdges(updatedEdges);
    }
  }, [edges, id, variables, setEdges]);

  useEffect(() => {
    const handleOpenVariableSettings = (event: any) => {
      const { varId, x, y } = event.detail;
      
      const tempAnchor = document.createElement('div');
      tempAnchor.style.position = 'absolute';
      tempAnchor.style.left = `${x}px`;
      tempAnchor.style.top = `${y}px`;
      document.body.appendChild(tempAnchor);
      
      setSettingsAnchorEl(tempAnchor);
      setActiveVariableId(varId);
      
      const cleanup = () => {
        document.body.removeChild(tempAnchor);
        document.removeEventListener('closeVariableSettings', cleanup);
      };
      
      document.addEventListener('closeVariableSettings', cleanup, { once: true });
    };
    
    document.addEventListener('openVariableSettings', handleOpenVariableSettings);

    return () => {
      document.removeEventListener('openVariableSettings', handleOpenVariableSettings);
    };
  }, []);

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
    setActiveVariableId(null);
    document.dispatchEvent(new CustomEvent('closeVariableSettings'));
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>, varId: string) => {
    event.stopPropagation();
    setSettingsAnchorEl(event.currentTarget as HTMLElement);
    setActiveVariableId(varId);
  };

  const getVariableSettings = (variableId: string) => {
    const variable = variables.find(v => v.id === variableId);
    return {
      dynamicHeight: true, 
      ...variable?.settings,
    };
  };

  const handleSettingChange = (variableId: string, settingName: string, value: any) => {
    const updatedVariables = variables.map(variable => {
      if (variable.id === variableId) {
        return {
          ...variable,
          settings: {
            ...variable.settings,
            [settingName]: value
          }
        };
      }
      return variable;
    });
    
    setVariables(updatedVariables);
    
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        variables: updatedVariables,
      },
      setSchema,
    });
  };

  const handleGridSettingsClick = (variableId: string) => {
    const settingsIcon = document.querySelector(`[data-variable-id="${variableId}"] button`);
    
    if (settingsIcon) {
      setSettingsAnchorEl(settingsIcon as HTMLElement);
      setActiveVariableId(variableId);
    } else {
      const tempAnchor = document.createElement('div');
      document.body.appendChild(tempAnchor);
      
      setSettingsAnchorEl(tempAnchor);
      setActiveVariableId(variableId);
      
      document.addEventListener('closeVariableSettings', function removeAnchor() {
        if (document.body.contains(tempAnchor)) {
          document.body.removeChild(tempAnchor);
        }
        document.removeEventListener('closeVariableSettings', removeAnchor);
      });
    }
  };

  const handleOpenConfigPopover = (event: React.MouseEvent<HTMLElement>) => {
    setConfigAnchorEl(event.currentTarget);
    setIsConfigPopoverOpen(true);
  };

  const handleCloseConfigPopover = () => {
    setIsConfigPopoverOpen(false);
    setConfigAnchorEl(null);
  };

  const handleOpenPreviewModal = () => {
    setIsPreviewModalOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  const onHeaderFooterChange = (e: { target: { name: string; value: string } }) => {
    setHeaderFooterFormData((prev: HeaderFooterFormData) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSaveConfig = () => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        headerFooterConfig: headerFooterFormData,
      },
      setSchema,
    });
    
    setNotification({
      type: 'success',
      message: 'Header and footer configuration saved successfully!',
    });
    
    handleCloseConfigPopover();
  };

  return (
    <Node
      id={id}
      data={data}
      type={type}
      size={size || data?.size}
      btnText="Run"
      onSubmit={onExecute}
      isLoading={isLoading}
      edges={edges}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: `${size.height}px`, minHeight: `${size.height}px`, overflow: 'visible' }}>
        
        <Box sx={{ borderBottom: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`, flexShrink: 0 }}>
          <StyledTabs 
            theme={user?.theme} 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            data-active-tab={activeTab}
          >
            <Tab label="Layout Designer" />
            <Tab label="Variables" />
          </StyledTabs>
        </Box>
        
        {(
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1, 
            overflow: activeTab === 0 ? 'hidden' : 'visible'
          }}>
            
            {activeTab === 0 && (
              <Box p="16px" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#667085',
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  Layout Designer
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                    fontSize: '12px',
                    marginBottom: '16px',
                    fontStyle: 'italic',
                  }}
                >
                  Drag and resize your content boxes. Switch to Variables tab to add more display variables.
                </Typography>
                
                <GridLayoutContainer theme={user?.theme} sx={{ 
                  flex: 1, 
                  minHeight: 0,
                  maxHeight: 'calc(100% - 60px)',
                  overflow: 'auto',
                  paddingBottom: '20px',
                  marginBottom: '20px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: user?.theme === 'dark' ? '#475467' : '#D0D5DD',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: user?.theme === 'dark' ? '#667085' : '#98A2B3',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: user?.theme === 'dark' ? '#475467 #1D2939' : '#D0D5DD #F9FAFB',
                }}>
                  <Box ref={gridContainerRef} sx={{ width: '100%', height: '100%' }}>
                    {variables.length > 0 && (
                      <ResponsiveGridLayout
                        className="layout nodrag"
                        layout={currentLayout}
                        cols={12}
                        rowHeight={50}
                        isDraggable={true}
                        isResizable={true}
                        onLayoutChange={handleLayoutChange}
                        onDragStop={(layout: Layout[], oldItem: Layout, newItem: Layout) => {
                          const constrainedItem = constrainLayoutItem(newItem);
                          if (constrainedItem.y !== newItem.y || constrainedItem.h !== newItem.h) {
                            const updatedLayout = layout.map((item: Layout) => 
                              item.i === newItem.i ? constrainedItem : constrainLayoutItem(item)
                            );
                            isGridChangeRef.current = false;
                            handleLayoutChange(updatedLayout);
                          }
                        }}
                        onResizeStop={(layout: Layout[], oldItem: Layout, newItem: Layout) => {
                          const constrainedItem = constrainLayoutItem(newItem);
                          if (constrainedItem.y !== newItem.y || constrainedItem.h !== newItem.h) {
                            const updatedLayout = layout.map((item: Layout) => 
                              item.i === newItem.i ? constrainedItem : constrainLayoutItem(item)
                            );
                            isGridChangeRef.current = false;
                            handleLayoutChange(updatedLayout);
                          }
                        }}
                        draggableHandle=".grid-item-drag-handle"
                        resizeHandles={['se']}
                        margin={[5, 5]}
                        containerPadding={[0, 0]}
                        compactType={null}
                        preventCollision={false}
                      >
                        {variables.map(variable => (
                          <div key={variable.id}>
                            <GridItem variable={variable} />
                          </div>
                        ))}
                      </ResponsiveGridLayout>
                    )}
                  </Box>
                </GridLayoutContainer>

                {variables.length === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      color: user?.theme === 'dark' ? '#667085' : '#98A2B3',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography sx={{ fontStyle: 'italic' }}>
                      No layout boxes to display. Add variables in the Variables tab first.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
            <Box p="16px" sx={{ overflow: 'visible' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  position: 'relative',
                  '& svg': { cursor: 'pointer' },
                  mb: !collapsed ? 0 : '8px',
                }}
              >
                <CaretDown
                  onClick={() => {
                    setCollapsed(!collapsed);
                    setTimeout(() => {
                      updateNodeInternals(id);
                    }, 50);
                  }}
                  size={16}
                  color={!collapsed ? '#3650DE' : '#667085'}
                />
                <Typography
                  fontSize={'16px'}
                  color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
                  fontWeight={600}
                >
                  Display Variables
                </Typography>
                {variables?.length < 10 && (
                  <IconButtonTooltip title="Add Variable" onClick={addVariable}>
                    <PlusCircle size={16} color="#98A2B3" />
                  </IconButtonTooltip>
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                  fontSize: '12px',
                  marginBottom: '20px',
                  fontStyle: 'italic',
                  display: !collapsed ? 'none' : 'block',
                }}
              >
                Add and configure your display variables. Each variable will appear as a content box in the Layout Designer.
              </Typography>

              <Box>
                {(variables || [])?.map((variable, index) => (
                  <Box
                    style={{
                      marginBottom: collapsed ? 20 : 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      position: 'relative',
                    }}
                    key={variable.id}
                  >
                    {activeTab === 1 && collapsed && (
                    <InputHandle
                      key={`${variable.id}`}
                      position={Position.Left}
                      type="target"
                      left={-26}
                      isActive={isActiveEdge(edges, id, variable.id, 'target')}
                      activeColor={findColor?.color}
                      top="50%"
                      handleId={variable.id}
                    />
                  )}
                    {collapsed && (
                      <>
                        <TextField
                          type="text"
                          placeholder="Variable Name"
                          name="name"
                          value={variable.name}
                          onChange={(e: React.ChangeEvent<any>) =>
                            handleVariableChange(e, variable.id)
                          }
                          InputProps={{
                            style: {
                              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            },
                          }}
                          sx={{
                            maxWidth: '150px',
                            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                            border: `2px solid ${
                              user?.theme === 'dark' ? '#475467' : '#EAECF0'
                            }`,
                          }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '120px' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}
                          >
                            {variable.textData?.length || 0} items connected
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: user?.theme === 'dark' ? '#667085' : '#98A2B3',
                              fontSize: '11px',
                            }}
                          >
                            Size: {variable.w}×{variable.h}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButtonTooltip
                            title="Display Settings"
                            onClick={(e: React.MouseEvent<HTMLElement>) => handleSettingsClick(e, variable.id)}
                        >
                            <Gear size={16} color="#98A2B3" />
                        </IconButtonTooltip>
                          
                          <Box 
                            sx={{
                              opacity: variables.length <= 1 ? 0.5 : 1,
                              pointerEvents: variables.length <= 1 ? 'none' : 'auto',
                            }}
                          >
                            <IconButtonTooltip
                              title={variables.length <= 1 ? "Cannot delete the last variable" : "Delete Variable"}
                              onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.stopPropagation();
                                deleteVariable(variable.id);
                              }}
                            >
                              <Trash size={16} color={variables.length <= 1 ? "#CCC" : "#98A2B3"} />
                            </IconButtonTooltip>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                ))}
              </Box>

              {variables.length === 0 && collapsed && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    color: user?.theme === 'dark' ? '#667085' : '#98A2B3',
                  }}
                >
                  <Typography sx={{ mb: 2, fontStyle: 'italic' }}>
                    No variables yet. Click the + button to add your first variable.
                  </Typography>
                </Box>
              )}
              
              {activeTab === 1 && !collapsed && variables.length > 0 && (
                <>
                  {variables.slice(1).map((variable) => (
                    <InputHandle
                      key={`variables-collapsed-hidden-${variable.id}`}
                      position={Position.Left}
                      type="target"
                      left={-10}
                      isActive={isActiveEdge(edges, id, variable.id, 'target')}
                      activeColor={findColor?.color}
                      top="170px" 
                      handleId={variable.id}
                      backgroundColorDefault="transparent"
                    />
                  ))}
                  
                  <InputHandle
                    key="variables-collapsed-primary-handle"
                    position={Position.Left}
                    type="target"
                    left={-10}
                    isActive={edges.some(edge => edge.target === id && variables.some(v => v.id === edge.targetHandle))}
                    activeColor={findColor?.color}
                    top="170px" 
                    handleId={variables[0]?.id}
                  />
                </>
              )}
            </Box>
          )}

          {activeTab === 0 && variables.length > 0 && (
            <>
              {variables.slice(1).map((variable) => (
                <InputHandle
                  key={`layout-designer-hidden-${variable.id}`}
                  position={Position.Left}
                  type="target"
                  left={-12}
                  isActive={isActiveEdge(edges, id, variable.id, 'target')}
                  activeColor={findColor?.color}
                  top="170px"
                  handleId={variable.id}
                  backgroundColorDefault="transparent" 
                />
              ))}
              
              <InputHandle
                key={`layout-designer-primary-handle`}
                position={Position.Left}
                type="target"
                left={-12}
                isActive={edges.some(edge => edge.target === id && variables.some(v => v.id === edge.targetHandle))}
                activeColor={findColor?.color}
                top="170px"
                handleId={variables[0]?.id} 
              />
            </>
          )}

          </Box>
        )}

        <Box sx={{ flexShrink: 0, borderTop: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}` }}>
          <Box p="16px">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <Box>
              <FormLabel sx={{
                color: user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
              }}>Save output and generate file link.</FormLabel>
              <BlueSwitch
                checked={saveOutput}
                onChange={onSaveOutputChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                value={filter}
                onChange={onChangeFilter}
                className="nodrag"
                size="small"
                sx={{
                  '& .MuiList-root': {
                    backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: 'none',
                  '.MuiOutlinedInput-notchedOutline': { border: 0 },
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                        backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    },
                  },
                }}
              >
                <MenuItem value="all" sx={{ color: user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)' }}>Intake all</MenuItem>
                <MenuItem value="code" sx={{ color: user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)' }}>Only Intake 'code'</MenuItem>
                <MenuItem value="table" sx={{ color: user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)' }}>Only Intake 'tables'</MenuItem>
              </Select>
              
              <IconButtonTooltip
                title="Preview Report"
                onClick={handleOpenPreviewModal}
                sx={{ padding: '8px' }}
              >
                <Eye size={20} color="#98A2B3" />
              </IconButtonTooltip>
              
              <IconButtonTooltip
                title="Header and Footer Configuration"
                onClick={handleOpenConfigPopover}
                sx={{ padding: '8px' }}
              >
                <Gear size={20} color="#98A2B3" />
              </IconButtonTooltip>
            </Box>

            <Box>
              <input
                type="number"   
                name="width"
                  placeholder="Width"
                min={200}
                value={size.width}
                onChange={onChangeInput}
                max={1000}
                className="nodrag"
                style={{
                  width: '60px',
                    border: `1px solid ${user?.theme === 'dark' ? '#344054' : '#D0D5DD'}`,
                    backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#98A2B3' : 'black',
                }}
              />
              <input
                type="number"
                name="height"
                min={200}
                  placeholder="Height"
                max={1000}
                value={size.height}
                onChange={onChangeInput}
                style={{
                  width: '60px',
                    border: `1px solid ${user?.theme === 'dark' ? '#344054' : '#D0D5DD'}`,
                    backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#98A2B3' : 'black',
                }}
                className="nodrag"
              />
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Button variant="contained" onClick={onDownload}>
              Download
            </Button>

            <Select
              onChange={onChangeSavingType}
              value={savingType}
              name="saveTypes"
              className="nodrag"
              sx={{
                height: 'unset',
                '& .MuiList-root': {
                  backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                },
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                        backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                      backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
                style: {
                  maxHeight: '700px',
                },
              }}
            >
              {saveTypes.map((saveType, index) => (
                <MenuItem key={index} value={saveType.name}>
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    {saveType.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Divider />

        <Box p="16px">
          {saveOutput && (
            <OutputTextarea
              value={data?.fileLink?.src || 'No Link generated'}
              activeHandle={isActiveEdge(edges, id, 'fileLink', 'source')}
              labelName="Link"
              handleId="fileLink"
              nodeLabel={data?.label}
              type={type}
              findColor={findColor}
              style={{
                marginBottom: 0
              }}
            />
          )}
          </Box>
        </Box>

      </Box>

      <InputHandle
        activeColor={findColor?.color}
        handleId="contentOut"
        isActive={isActiveEdge(edges, id, 'contentOut', 'source')}
        right={-9}
        top={'160px'}
        position={Position.Right}
        type="source"
      />
      
      <Popover
        id={popOverSettingsId}
        open={openSettingsPopover}
        anchorEl={settingsAnchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            width: '300px',
            border: '1px solid var(--Neutral-300, #D0D5DD)',
            padding: '16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              fontSize="16px"
              fontWeight={600}
              lineHeight={'24px'}
            >
              Display Settings
            </Typography>
            <IconButton
              sx={{ padding: '4px' }}
              size="medium"
              onClick={handleSettingsClose}
            >
              <XCircle color="#667085" />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography fontSize="14px" fontWeight={500}>
                  Dynamic Height
                </Typography>
                <Typography fontSize="12px" color="#667085">
                  Automatically adjust height based on content
                </Typography>
              </Box>
              <Switch
                checked={getVariableSettings(activeVariableId || '')?.dynamicHeight || false}
                onChange={(e) => handleSettingChange(activeVariableId || '', 'dynamicHeight', e.target.checked)}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Popover>
      <HeaderFooterReportBuilder
        open={isConfigPopoverOpen}
        anchorEl={configAnchorEl}
        onClose={handleCloseConfigPopover}
        nodeId={id}
        onSave={handleSaveConfig}
        formData={headerFooterFormData}
        onChange={onHeaderFooterChange}
      />
      
      <PreviewModal
        show={isPreviewModalOpen}
        onHide={handleClosePreviewModal}
        data={{
          value: generateHTMLStructure(),
          labelName: 'Report Preview',
          nodeLabel: data?.label || 'Report Builder',
        }}
      />
    </Node>
  );
}

export default memo(ReportBuilderNode); 